<template>
  <b-modal ref="modal" @hidden="flush" centered no-close-on-backdrop>
    <template #modal-header="{ close }">
      <h5 class="font-weight-bolder m-0">
        <span v-if="id">Update Group</span>
        <span v-else>New Group</span>
      </h5>

      <b-button
        size="sm"
        variant="none"
        class="btn-icon btn-circle btn-hover-dark"
        title="Close"
        @click="close"
        v-b-tooltip.hover
      >
        <i class="material-icons icon-xl">close</i>
      </b-button>
    </template>

    <template #default>
      <validation-observer ref="observer">
        <div
          class="alert text-center font-weight-bold"
          :class="variants"
          v-html="error || '&nbsp;'"
          v-if="error && error.length"
        />
        <div class="row">
          <div class="col-12">
            <validation-provider
              name="name"
              rules="required|max:48"
              v-slot="{ errors }"
            >
              <b-form-group label="Name *" label-for="name">
                <b-form-input :disabled="disabled" v-model="object.name" />
                <b-form-text
                  text-variant="danger"
                  :key="key"
                  v-for="(error, key) in errors"
                  >{{ error }}</b-form-text
                >
              </b-form-group>
            </validation-provider>

            <div class="row align-items-center justify-content-center">
              <div class="col-5">
                <div class="mb-2">Available Permissions</div>
                <div class="permission-block">
                  <div
                    v-for="(permission, i) in permissions.filter(
                      (each) => each.available
                    )"
                    :key="i"
                    class="permission-entity"
                    :style="getPermissionBlockStyle(permission)"
                    @click="selectPermission(permission)"
                  >
                    {{ permission.label }}
                  </div>
                </div>
              </div>
              <div class="col-2">
                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  :disabled="!permissions.find((each) => each.available)"
                  @click.stop="moveAllAvailableToSelected"
                >
                  <i class="material-icons icon-xl"
                    >keyboard_double_arrow_right</i
                  >
                </button>
                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  :disabled="
                    !permissions.find((each) => each.selected && each.available)
                  "
                  @click.stop="moveAvailableToSelected"
                >
                  <i class="material-icons icon-xl">keyboard_arrow_right</i>
                </button>
                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  :disabled="
                    !permissions.find(
                      (each) => each.selected && !each.available
                    )
                  "
                  @click.stop="moveSelectedToAvailble"
                >
                  <i class="material-icons icon-xl">keyboard_arrow_left</i>
                </button>
                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  :disabled="!permissions.find((each) => !each.available)"
                  @click.stop="moveAllSelectedToAvailble"
                >
                  <i class="material-icons icon-xl"
                    >keyboard_double_arrow_left</i
                  >
                </button>
              </div>
              <div class="col-5">
                <div class="mb-2">Selected Permissions</div>
                <div class="permission-block">
                  <div
                    v-for="(permission, i) in permissions.filter(
                      (each) => !each.available
                    )"
                    :key="i"
                    class="permission-entity"
                    :style="getPermissionBlockStyle(permission)"
                    @click="selectPermission(permission)"
                  >
                    {{ permission.label }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </validation-observer>
    </template>

    <template #modal-footer>
      <div class="d-flex justify-content-between mx-auto">
        <b-button
          class="btn-pill font-weight-bold"
          variant="dark"
          :disabled="disabled"
          :class="{
            'spinner spinner-success spinner-lg spinner-left': sending,
          }"
          @click.stop="submit"
        >
          <span v-if="sending">Submitting...</span>
          <span v-else>Submit</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

import GroupsService from "@/services/groups.service";
import PermissionsService from "@/services/permissions.service";
import CountriesDropdown from "@/components/countries.dropdown";
import CurrenciesDropdown from "@/components/currencies.dropdown";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "GroupForm",
  components: {
    ValidationObserver,
    ValidationProvider,
    CountriesDropdown,
    CurrenciesDropdown,
  },
  data() {
    return {
      id: null,
      error: null,
      fetching: false,
      sending: false,
      permissions: [],
      selectedPermissions: [],
      object: {
        name: null,
      },
    };
  },
  methods: {
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    selectPermission(permission) {
      this.permissions = this.permissions.map((each) => {
        if (each.name === permission.name) {
          each.selected = !each.selected;
        }
        return each;
      });
    },
    getPermissionBlockStyle(permission) {
      if (permission.selected) {
        return `background-color:#e4e6ef;`;
      } else {
        return `background-color:white;`;
      }
    },
    moveAllAvailableToSelected() {
      this.permissions = this.permissions.map((each) => {
        if (each.available) {
          each.available = false;
        }
        return each;
      });
    },
    moveAvailableToSelected() {
      this.permissions = this.permissions.map((each) => {
        if (each.selected && each.available) {
          each.available = false;
          each.selected = false;
        }
        return each;
      });
    },
    moveAllSelectedToAvailble() {
      this.permissions = this.permissions.map((each) => {
        if (!each.available) {
          each.available = true;
        }
        return each;
      });
    },
    moveSelectedToAvailble() {
      this.permissions = this.permissions.map((each) => {
        if (each.selected && !each.available) {
          each.available = true;
          each.selected = false;
        }
        return each;
      });
    },
    async fetch() {
      this.error = null;
      this.fetching = true;

      GroupsService.get(this.id)
        .then(({ data }) => {
          this.set(data);
        })
        .catch(({ message }) => {
          this.error = formatErrorMessage(message);
        })
        .finally(() => {
          this.fetching = false;
        });
    },
    async create(payload) {
      GroupsService.post(payload)
        .then(({ data }) => {
          this.$refs["modal"].hide();
          this.$parent.query();
        })
        .catch(({ message }) => {
          this.error = formatErrorMessage(message);
        })
        .finally(() => {
          this.sending = false;
        });
    },
    async update(payload) {
      GroupsService.patch(this.id, payload)
        .then(({ data }) => {
          this.$refs["modal"].hide();
          this.$parent.query();
        })
        .catch(({ message }) => {
          this.error = formatErrorMessage(message);
        })
        .finally(() => {
          this.sending = false;
        });
    },
    async submit() {
      const valid = await this.$refs.observer.validate();

      if (!valid) return;

      this.error = null;
      this.sending = true;

      const payload = {
        name: this.object?.name,
        permissions: this.permissions
          .filter((each) => !each.available)
          .map((each) => each.name),
      };

      if (this.id) return this.update(payload);

      return this.create(payload);
    },
    set(data) {
      if (data.name) {
        this.object["name"] = data.name;
      }
      if (data.permissions.length) {
        this.permissions = this.permissions.map((each) => {
          if (data.permissions.indexOf(each.name) >= 0) {
            each.available = false;
          }
          return each;
        });
      }
    },
    reset() {
      if (this.$refs.observer) this.$refs.observer.reset();

      this.object = {
        name: null,
      };
    },
    show(id) {
      this.$refs["modal"].show();

      this.id = id || null;

      if (this.id) this.fetch();
    },
    flush() {
      this.fetching = false;
      this.sending = false;
      this.error = null;
      this.permissions = this.permissions.map((each) => {
        each.selected = false;
        each.available = true;
        return each;
      });
      this.reset();
    },
  },
  computed: {
    disabled() {
      return this.sending || this.fetching;
    },
    variants() {
      return {
        show: this.error,
        "alert-light-danger": this.error,
      };
    },
  },
  mounted() {
    this.querying = true;
    PermissionsService.query()
      .then(({ data }) => {
        this.permissions = data.map((each) => {
          return {
            name: each,
            label:
              this.capitalizeFirstLetter(each.split(":")[1]) +
              " " +
              this.capitalizeFirstLetter(each.split(":")[0]),
            selected: false,
            available: true,
          };
        });
      })
      .catch(({ message }) => {
        this.error = "There was an error loading permissions.";
      })
      .finally(() => {
        this.querying = false;
      });
  },
};
</script>

<style scoped>
.permission-block {
  height: 250px;
  overflow: auto;
  border: 1px solid #e4e6ef;
  background-color: #ffffff;
  border-radius: 10px;
}
.permission-entity {
  margin: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 8px;
}
</style>
